/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import * as React from "react";
import AttachedFileItem from "../Common/AttachedFileItem";
import SpectraDeviceSelect, {
  SpectraDevice,
} from "../Common/SpectraDeviceSelect";
import {
  autoFillChemValues,
  ChemicalFields,
  CropTypeCodeToName,
  CropTypes,
  findDbFieldNames,
} from "../../../types/chemicals";
import SampleMaterialTypeSelect from "../Common/SampleMaterialTypeSelect";
import ProgressButton from "../Common/ProgressButton";
import sampleRecordService from "../../../service/sampleRecord.service";
import { showSnackbar } from "../../../service/snackbar.service";
import {
  ISampleRecordCreationPayload,
  ISampleRecordPopulated,
} from "../../../types/sample";
import { ISampleSpectraRecord } from "../../../types/spectra";
import { getSpectraDataId } from "../../../helpers/spectra";
import { useTranslation } from "react-i18next";
import AnyAssetFileUploader, {
  AwsFileDirectory,
  AwsLocation,
} from "../Common/AnyAssetFileUploader";
import Button from "@mui/material/Button";
import { cloneDeep, uniqBy } from "lodash";
import { camelCaseToHumanReadable } from "../../../helpers/utils";
import {
  detectSpectraDeviceFromSamples,
} from "../../../helpers/spectraDevice";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Checkbox from "@mui/material/Checkbox";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import UploadChemicalAnalysisDataDialog from "../UploadChemicalAnalysisDataDialog";
import { useAppContext } from "../../../contexts/useStorageContext";
import {
  findCropTypeByNameOrAlias,
  resolveCropTypeName,
} from "../../../types/cropType";
import { Chip, Stack } from "@mui/material";
import additiveAlgorithmService from "../../../service/additiveAlgorithm.service";
import { IAdditiveEffectAlgorithm } from "../../../types/additiveAlgorithm";
import LightTooltip from "../Common/LightTooltip";
import AdditiveEffectElement from "../AdditiveAlgorithm/AdditiveEffectElement";
const FieldSection = styled(Box)(() => ({
  marginBottom: "24px",
}));

const Label = styled(Box)(() => ({
  fontWeight: "500",
  marginBottom: "8px",
  color: "#888",
}));

enum PredictionTask {
  NAG = "NAG Prediction",
  FTI = "FTI Prediction",
}

export default function PredictionFetchFormDialog({
  rawSpectraFileUrl,
  runningSamples: initialRunningSamples,
  onClose,
  onSuccess,
  onCancelFile,
}: {
  rawSpectraFileUrl?: string;
  runningSamples?: ISampleRecordPopulated[];
  onClose: () => void;
  onSuccess: (
    parsed: ISampleRecordCreationPayload[],
    onlySaveSpectraData?: boolean,
    appliedAdditives?: IAdditiveEffectAlgorithm[],
    spectraDevice?: SpectraDevice
  ) => void;
  onCancelFile?: () => void;
}) {
  const [runningSamples, setRunningSamples] = React.useState<
    ISampleRecordPopulated[]
  >(initialRunningSamples || []);
  const { state } = useAppContext();
  const { myCompanyProfile, cropTypes = [] } = state;
  const allowedMaterialTypes = cropTypes;
  const { t } = useTranslation();
  const [spectraDevice, setSpectraDevice] = React.useState<
    SpectraDevice | undefined
  >(undefined);
  const [materialType, setMaterialType] = React.useState<CropTypes | undefined>(
    uniqBy(
      (runningSamples || [])?.filter((s) => s.type),
      (s) => s.type?.toLowerCase()
    ).map((s) => s.type)?.length == 1
      ? (Object.values(CropTypes).find(
          (c) => c.toLowerCase() == runningSamples?.[0]?.type?.toLowerCase()
        ) as CropTypes | undefined)
      : undefined
  );
  const [deviceId, setDeviceId] = React.useState<string | undefined>(
    uniqBy(
      (runningSamples || [])?.filter((s) => s.spectraDeviceId),
      (s) => s.spectraDeviceId?.toLowerCase()
    ).length == 1
      ? runningSamples?.find((s) => s.spectraDeviceId)?.spectraDeviceId
      : undefined
  );
  const [predictionProcessing, setPredictionProcessing] = React.useState(false);
  const [calibrationFileUrls, setCalibrationFileUrls] = React.useState<
    string[] | undefined
  >();
  const [openUploadFileDialog, setOpenUploadFileDialog] = React.useState(false);
  const [predictionTasks, setPredictionTasks] = React.useState<
    PredictionTask[]
  >([PredictionTask.NAG, PredictionTask.FTI]);
  const [
    openUpdateChemicalAnalysisDialog,
    setOpenUpdateChemicalAnalysisDialog,
  ] = React.useState(false);
  const [parsedSampleRecords, setParsedSampleRecords] = React.useState<
    ISampleRecordCreationPayload[]
  >([]);
  const [appliedAdditives, setAppliedAdditives] = React.useState<
    IAdditiveEffectAlgorithm[]
  >([]);
  const [selectedAdditiveIds, setSelectedAdditiveIds] = React.useState<
    string[]
  >([]);
  const [availableAdditives, setAvailableAdditives] = React.useState<
    IAdditiveEffectAlgorithm[]
  >([]);

  const getPrediction = async (tasks: PredictionTask[]) => {
    if (!materialType) return;
    const samplesForPrediction = runningSamples?.filter((s) => {
      if (!s.spectraData) return false;
      if (
        deviceId &&
        s.spectraDeviceId?.toLowerCase() !== deviceId.toLowerCase()
      ) {
        return false;
      }
      return true;
    });
    if (!samplesForPrediction?.length && !rawSpectraFileUrl) return;
    setPredictionProcessing(true);
    try {
      const fieldNames: string[] = [];
      ChemicalFields.filter((chem) => chem.types?.includes(materialType)).map(
        (c) => {
          fieldNames.push(c.fieldName);
          fieldNames.push(...(c.aliases || []));
        }
      );
      const fetchedSpectraData: ISampleSpectraRecord[] = !samplesForPrediction?.length
        ? []
        : await sampleRecordService.getSpectraData(
            samplesForPrediction.map((r) => getSpectraDataId(r.spectraData))
          );
      const spectraParams: { [key: string]: number[] } = {};
      const spectraSampleNumberToUid: { [key: string]: number | undefined } =
        {};
      const spectraSampleNumberToCompanyUid: {
        [key: string]: number | undefined;
      } = {};
      samplesForPrediction?.map((s) => {
        spectraSampleNumberToCompanyUid[s.sampleNumber || ""] = s.companyUid;
        spectraSampleNumberToUid[s.sampleNumber || ""] = s.uid;
        spectraParams[`${s.sampleNumber}`] =
          fetchedSpectraData?.find(
            (f) => f._id == getSpectraDataId(s.spectraData)
          )?.values || [];
      });
      const apiParams: any = {
        task: tasks?.length ? tasks : "Source",
        device: spectraDevice,
        material: materialType,
        constituents: [], //fieldNames,
        additiveIds: selectedAdditiveIds,
      };
      if (deviceId) {
        apiParams.deviceId = deviceId;
      }
      if (rawSpectraFileUrl) {
        apiParams.url = rawSpectraFileUrl;
        apiParams.sampleUid = samplesForPrediction?.[0]?.uid;
      } else if (fetchedSpectraData?.length) {
        apiParams.spectra = spectraParams;
        apiParams.wavelength = fetchedSpectraData?.find(
          (f) => f._id == getSpectraDataId(samplesForPrediction?.[0]?.spectraData)
        )?.wavelengths;
      }
      const result = await sampleRecordService.getPrediction(apiParams);
      const appliedAdditivesFromResult: IAdditiveEffectAlgorithm[] =
        result?.additives || [];
      setAppliedAdditives(appliedAdditivesFromResult);
      const sampleNumbers =
        result?.labnum || Object.keys(result?.spectra || {}) || [];
      if (!sampleNumbers.length) {
        showSnackbar(
          "danger",
          "Error",
          "Could not read sample numbers from the prediction."
        );
        return;
      }
      const predictions = result?.predictions || {};
      const classPredictions = result?.class || [];
      if (!predictions) {
        showSnackbar("danger", "", "Could not read the prediction.");
        return;
      }
      const spectraData = result?.spectra;
      const wavelengths = result?.wavelength;
      const parsedSampleRecords: ISampleRecordPopulated[] = [];
      sampleNumbers.map((sampleNumber: string, i: number) => {
        const chemData: { [key: string]: number } = {};        
        Object.keys(predictions).map((key) => {
          if (!predictions[key]?.some((val: number) => val)) return;
          const chemValue = predictions[key]?.[i];
          const dbFieldName = findDbFieldNames(key)?.[0];
          if(dbFieldName && !(chemData[dbFieldName])){
            let valueToInput = isNaN(Number(chemValue))
              ? undefined
              : Number(chemValue);
            if (!valueToInput) return;
            if (
              dbFieldName == "Slow_Pool" &&
              valueToInput &&
              valueToInput > 9
            ) {
              valueToInput = valueToInput / 2;
            }
            chemData[dbFieldName] = valueToInput;
          } else {
            chemData[key] = chemValue;
          }
        });
        const spectraValues = spectraData?.[sampleNumber] || [];
        parsedSampleRecords.push({
          uid: spectraSampleNumberToUid[sampleNumber] as any,
          companyUid: spectraSampleNumberToCompanyUid[sampleNumber],
          sampleNumber,
          type: materialType,
          chemicals: autoFillChemValues(chemData),
          wavelengths,
          values: spectraValues,
          predictionAiModel: result?.predictionAiModel,
          predictionAiModels: result?.predictionAiModels,
          predictedType: classPredictions?.[i]
            ? (CropTypeCodeToName as any)[
                Number(classPredictions[i]).toString()
              ]
            : undefined,
          scannerCalibrationFiles: calibrationFileUrls,
        });
      });
      if (rawSpectraFileUrl) {
        onSuccess(
          parsedSampleRecords,
          tasks?.length ? false : true,
          appliedAdditivesFromResult,
          spectraDevice
        );
      } else {
        const updatedSampleRecordsData: ISampleRecordPopulated[] =
          parsedSampleRecords
            .map((s) => {
              const existingSample = runningSamples?.find((ss) => {
                return (
                  ss.sampleNumber?.toString() == s.sampleNumber?.toString()
                );
              });
              if (existingSample) {
                const newChemicals = s.chemicals;
                const existingChemicals = existingSample.chemicals;
                const updatedChemicals = {
                  ...existingChemicals,
                  ...newChemicals,
                };
                const clonedParsedSample = cloneDeep(s);
                delete clonedParsedSample.sampleNumber;
                delete clonedParsedSample.wavelengths;
                delete clonedParsedSample.values;
                delete clonedParsedSample.chemicals;
                return {
                  ...existingSample,
                  ...clonedParsedSample,
                  chemicals: updatedChemicals,
                };
              } else {
                return undefined as any;
              }
            })
            ?.filter((v) => v) || [];
        setParsedSampleRecords(updatedSampleRecordsData);
        setOpenUpdateChemicalAnalysisDialog(true);
      }

      setPredictionProcessing(false);
    } catch (error: any) {
      showSnackbar("danger", "", error?.response?.data?.message || "Could not fetch the prediction.");
      setPredictionProcessing(false);
    }
  };

  const numberOfSamplesWithSpectraData = React.useMemo(
    () =>
      runningSamples?.filter((s) => {
        if (!s.spectraData) return false;
        if (
          deviceId &&
          s.spectraDeviceId?.toLowerCase() !== deviceId.toLowerCase()
        ) {
          return false;
        }
        return true;
      }).length,
    [runningSamples, deviceId]
  );
  const numberOfSamplesWithoutSpectraData = React.useMemo(
    () => runningSamples?.filter((s) => !s.spectraData).length,
    [runningSamples]
  );
  const uniqueDeviceIds = React.useMemo(
    () =>
      uniqBy(
        runningSamples.filter((s) => s.spectraDeviceId),
        (s) => s.spectraDeviceId?.toLowerCase()
      ).map((s) => s.spectraDeviceId as string),
    [runningSamples]
  );
  const requiresDeviceIdSelection = uniqueDeviceIds.length > 1;
  const samplesForSpectraDeviceDetection = React.useMemo(
    () =>
      runningSamples.filter((s) => {
        if (
          deviceId &&
          s.spectraDeviceId?.toLowerCase() !== deviceId.toLowerCase()
        ) {
          return false;
        }
        return true;
      }),
    [runningSamples, deviceId]
  );
  const autoDetectedSpectraDevice = React.useMemo(
    () => detectSpectraDeviceFromSamples(samplesForSpectraDeviceDetection),
    [samplesForSpectraDeviceDetection]
  );

  React.useEffect(() => {
    if (autoDetectedSpectraDevice) {
      setSpectraDevice(autoDetectedSpectraDevice);
    }
  }, [autoDetectedSpectraDevice]);

  const fetchCompanyAvailableAdditives = async () => {
    const additives =
      await additiveAlgorithmService.getCompanyAvailableAdditives();
    setAvailableAdditives(additives);
  };

  React.useEffect(() => {
    if (materialType || !allowedMaterialTypes.length) return;
    const sampleTypes = uniqBy(
      (runningSamples || []).filter((s) => s.type),
      (s) => s.type?.toLowerCase()
    ).map((s) => s.type);
    if (sampleTypes.length !== 1 || !sampleTypes[0]) return;
    const resolved = resolveCropTypeName(sampleTypes[0], allowedMaterialTypes);
    if (resolved) {
      setMaterialType(resolved as CropTypes);
    }
  }, [allowedMaterialTypes, runningSamples]);

  React.useEffect(() => {
    if (
      !materialType ||
      findCropTypeByNameOrAlias(materialType, allowedMaterialTypes)
        ?.isPublic
    )
      return;
    const selectedCropType = findCropTypeByNameOrAlias(
      materialType,
      allowedMaterialTypes
    );
    let updatedTasks = cloneDeep(predictionTasks);
    if (
      !selectedCropType?.availablePredictionTargets?.find((t) => t == "NAG") ||
      !myCompanyProfile?.allowedPredictionTargetsForCropTypes
        ?.find(
          (c) =>
            allowedMaterialTypes?.find((m) => m._id == c.cropTypeId)?.name ==
            selectedCropType?.name
        )
        ?.predictionTargets?.includes("NAG")
    ) {
      updatedTasks = updatedTasks.filter((t) => t !== PredictionTask.NAG);
    }
    if (
      !selectedCropType?.availablePredictionTargets?.find((t) => t == "FTI") ||
      !myCompanyProfile?.allowedPredictionTargetsForCropTypes
        ?.find(
          (c) =>
            allowedMaterialTypes?.find((m) => m._id == c.cropTypeId)?.name ==
            selectedCropType?.name
        )
        ?.predictionTargets?.includes("FTI")
    ) {
      updatedTasks = updatedTasks.filter((t) => t !== PredictionTask.FTI);
    }
    setPredictionTasks(updatedTasks);
  }, [myCompanyProfile, materialType, allowedMaterialTypes]);

  React.useEffect(() => {
    fetchCompanyAvailableAdditives();
  }, []);

  return (
    <Dialog open maxWidth="xs">
      <Box sx={{ width: "1000px", maxWidth: "100%" }}>
        <Box
          p={2}
          pb={0}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Typography variant="h6">{t("Get Prediction")}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box p={2}>
          {rawSpectraFileUrl ? (
            <FieldSection>
              <Label>{t("Raw Spectra File")}</Label>
              <AttachedFileItem
                url={rawSpectraFileUrl}
                onRemove={onCancelFile}
              />
            </FieldSection>
          ) : runningSamples?.length ? (
            <FieldSection>
              <Label>{t("Samples to run prediction")}</Label>
              {uniqBy(
                runningSamples.filter((s) => s.type),
                (s) => s.type?.toLowerCase()
              ).map((s) => s.type)?.length > 1 ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <div>
                    <div style={{ marginBottom: "8px" }}>
                      {t("One material type at a time. Select one please.")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      {uniqBy(
                        runningSamples.filter((s) => s.type),
                        (s) => s.type?.toLowerCase()
                      )
                        .map((s) => s.type)
                        .map((t) => (
                          <Chip
                            sx={{
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              const filteredSamples = runningSamples.filter(
                                (s) =>
                                  s.type?.toLowerCase() == t?.toLowerCase()
                              );
                              setRunningSamples(filteredSamples);
                              setMaterialType(
                                Object.values(CropTypes).find(
                                  (c) => c.toLowerCase() == t?.toLowerCase()
                                ) as CropTypes | undefined
                              );
                              const remainingDeviceIds = uniqBy(
                                filteredSamples.filter(
                                  (s) => s.spectraDeviceId
                                ),
                                (s) => s.spectraDeviceId?.toLowerCase()
                              );
                              setDeviceId(
                                remainingDeviceIds.length === 1
                                  ? remainingDeviceIds[0].spectraDeviceId
                                  : undefined
                              );
                            }}
                            key={t}
                            label={`${t} (${
                              runningSamples
                                .filter((s) => s.type)
                                .filter(
                                  (s) =>
                                    s.type?.toLowerCase() == t?.toLowerCase()
                                ).length
                            })`}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                    </div>
                  </div>
                </Alert>
              ) : null}
              {requiresDeviceIdSelection ? (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <div>
                    <div style={{ marginBottom: "8px" }}>
                      {t("One device ID at a time. Select one please.")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        flexWrap: "wrap",
                      }}
                    >
                      {uniqueDeviceIds.map((id) => (
                        <Chip
                          sx={{
                            cursor: "pointer",
                          }}
                          color={
                            deviceId?.toLowerCase() === id.toLowerCase()
                              ? "primary"
                              : "default"
                          }
                          onClick={() => {
                            const filteredSamples = runningSamples.filter(
                              (s) =>
                                s.spectraDeviceId?.toLowerCase() ==
                                id?.toLowerCase()
                            );
                            setRunningSamples(filteredSamples);
                            setDeviceId(id);
                            const remainingMaterialTypes = uniqBy(
                              filteredSamples.filter((s) => s.type),
                              (s) => s.type?.toLowerCase()
                            );
                            if (remainingMaterialTypes.length === 1) {
                              setMaterialType(
                                Object.values(CropTypes).find(
                                  (c) =>
                                    c.toLowerCase() ==
                                    remainingMaterialTypes[0].type?.toLowerCase()
                                ) as CropTypes | undefined
                              );
                            }
                          }}
                          key={id}
                          label={`${id} (${
                            runningSamples.filter(
                              (s) =>
                                s.spectraDeviceId?.toLowerCase() ==
                                id?.toLowerCase()
                            ).length
                          })`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </div>
                  </div>
                </Alert>
              ) : null}
              <Paper sx={{ padding: "8px 12px", border: "solid 1px #e2e2e2" }}>
                <Box sx={{ color: "success.main", fontWeight: "500" }}>
                  {`${numberOfSamplesWithSpectraData} ${t(
                    "Samples with spectra data"
                  )}`}
                </Box>
                {numberOfSamplesWithoutSpectraData ? (
                  <Box sx={{ mt: 1, color: "error.main", fontWeight: "500" }}>
                    {`${numberOfSamplesWithoutSpectraData} ${t(
                      "Samples don't have spectra data (will be skipped)"
                    )}`}
                  </Box>
                ) : null}
              </Paper>
            </FieldSection>
          ) : (
            <Alert severity="error">{t("No spectra data provided")}</Alert>
          )}
          <Stack direction="row" spacing={2} sx={{ display: "flex" }}>
            <FieldSection sx={{ flex: 1 }}>
              <Label>{t("Spectra Device")}</Label>
              {autoDetectedSpectraDevice ? (
                <Paper
                  sx={{
                    padding: "8px 12px",
                    border: "solid 1px #e2e2e2",
                    color: "success.main",
                    fontWeight: 500,
                  }}
                >
                  {camelCaseToHumanReadable(autoDetectedSpectraDevice)}
                </Paper>
              ) : (
                <SpectraDeviceSelect
                  error={!spectraDevice}
                  allowedDevices={
                    rawSpectraFileUrl?.endsWith(".nir")
                      ? [SpectraDevice.BlueSun, SpectraDevice.Foss]
                      : undefined
                  }
                  value={spectraDevice}
                  onChange={(newVal) => setSpectraDevice(newVal)}
                />
              )}
            </FieldSection>
            <FieldSection sx={{ flex: 1 }}>
              <Label>{t("Material Type")}</Label>
              <>
                <SampleMaterialTypeSelect
                  error={!materialType}
                  value={materialType}
                  onChange={(newVal) => setMaterialType(newVal)}
                />
              </>
            </FieldSection>
          </Stack>
          {availableAdditives?.length ? (
            <FieldSection>
              <Label
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {`${t("Additives")}: `}
                {availableAdditives?.map((a) => (
                  <Chip
                    key={a._id}
                    color={
                      selectedAdditiveIds.includes(a._id)
                        ? "success"
                        : "default"
                    }
                    label={
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        {selectedAdditiveIds.includes(a._id) ? (
                          <CheckCircleIcon
                            color="success"
                            sx={{ ml: "-6px" }}
                          />
                        ) : (
                          <RadioButtonUncheckedIcon
                            sx={{ ml: "-6px", opacity: 0.5 }}
                          />
                        )}
                        <Box>{a.name}</Box>
                      </div>
                    }
                    variant="outlined"
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setSelectedAdditiveIds(
                        selectedAdditiveIds.includes(a._id)
                          ? selectedAdditiveIds.filter((id) => id !== a._id)
                          : [...selectedAdditiveIds, a._id]
                      );
                    }}
                  />
                ))}
              </Label>
            </FieldSection>
          ) : null}

          <FieldSection>
            <Label>
              {`${t("Device Calibration File")}: `}
              <Button
                sx={{ ml: 2 }}
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenUploadFileDialog(true)}
              >
                {t("Add File")}
              </Button>
            </Label>
            <>
              {calibrationFileUrls?.map((url) => (
                <AttachedFileItem
                  url={url}
                  onRemove={() => {
                    setCalibrationFileUrls(
                      calibrationFileUrls?.filter((u) => u !== url)
                    );
                  }}
                />
              ))}
            </>
          </FieldSection>
          <FormGroup row sx={{ mt: "-16px" }}>
            {findCropTypeByNameOrAlias(materialType, allowedMaterialTypes)
              ?.availablePredictionTargets?.find((t) => t == "NAG") ? (
              <FormControlLabel
                disabled={
                  findCropTypeByNameOrAlias(materialType, allowedMaterialTypes)
                    ?.isPublic
                    ? false
                    : !myCompanyProfile?.allowedPredictionTargetsForCropTypes
                        ?.find(
                          (c) =>
                            c.cropTypeId ==
                            findCropTypeByNameOrAlias(
                              materialType,
                              allowedMaterialTypes
                            )?._id
                        )
                        ?.predictionTargets?.includes("NAG")
                }
                control={
                  <Checkbox
                    checked={predictionTasks.includes(PredictionTask.NAG)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPredictionTasks([
                          ...predictionTasks,
                          PredictionTask.NAG,
                        ]);
                      } else {
                        setPredictionTasks(
                          predictionTasks.filter(
                            (t) => t !== PredictionTask.NAG
                          )
                        );
                      }
                    }}
                  />
                }
                label={t("NAG Prediction")}
              />
            ) : null}
            {findCropTypeByNameOrAlias(materialType, allowedMaterialTypes)
              ?.availablePredictionTargets?.find((t) => t == "FTI") ? (
              <FormControlLabel
                disabled={
                  findCropTypeByNameOrAlias(materialType, allowedMaterialTypes)
                    ?.isPublic
                    ? false
                    : !myCompanyProfile?.allowedPredictionTargetsForCropTypes
                        ?.find(
                          (c) =>
                            c.cropTypeId ==
                            findCropTypeByNameOrAlias(
                              materialType,
                              allowedMaterialTypes
                            )?._id
                        )
                        ?.predictionTargets?.includes("FTI")
                }
                control={
                  <Checkbox
                    checked={predictionTasks.includes(PredictionTask.FTI)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setPredictionTasks([
                          ...predictionTasks,
                          PredictionTask.FTI,
                        ]);
                      } else {
                        setPredictionTasks(
                          predictionTasks.filter(
                            (t) => t !== PredictionTask.FTI
                          )
                        );
                      }
                    }}
                  />
                }
                label={t("FTI Prediction")}
              />
            ) : null}
          </FormGroup>
          <Box>
            <ProgressButton
              disabled={
                !predictionTasks.length ||
                !spectraDevice ||
                !materialType ||
                (requiresDeviceIdSelection && !deviceId) ||
                (!rawSpectraFileUrl && !runningSamples?.length) ||
                (!!runningSamples?.length && !numberOfSamplesWithSpectraData)
              }
              inProgress={predictionProcessing}
              title={t("Get Prediction")}
              fullWidth
              onClick={() => getPrediction(predictionTasks)}
            />
            <ProgressButton
              sx={{ mt: 1 }}
              color="warning"
              disabled={
                !spectraDevice ||
                !materialType ||
                (requiresDeviceIdSelection && !deviceId) ||
                (!rawSpectraFileUrl && !runningSamples?.length) ||
                (!!runningSamples?.length && !numberOfSamplesWithSpectraData)
              }
              inProgress={predictionProcessing}
              title={t("Or Just Save Spectra Data")}
              fullWidth
              onClick={() => getPrediction([])}
            />
          </Box>
        </Box>
      </Box>
      {openUploadFileDialog ? (
        <AnyAssetFileUploader
          location={AwsLocation.PrivateAssets}
          directory={AwsFileDirectory.SpectraCalibration}
          open={openUploadFileDialog}
          onClose={() => setOpenUploadFileDialog(false)}
          onSave={(urls) => {
            if (urls?.[0]) {
              setCalibrationFileUrls(
                uniqBy([...(calibrationFileUrls || []), ...urls], (v) => v)
              );
            }
            setOpenUploadFileDialog(false);
          }}
          title={t("Upload Device Calibration File")}
        />
      ) : null}
      {openUpdateChemicalAnalysisDialog ? (
        <UploadChemicalAnalysisDataDialog
          spectraDevice={spectraDevice}
          onClose={() => setOpenUpdateChemicalAnalysisDialog(false)}
          initialItems={parsedSampleRecords}
          onlyUpdate
          onSuccess={() => {
            setOpenUpdateChemicalAnalysisDialog(false);
            onSuccess(parsedSampleRecords);
          }}
          appliedAdditives={appliedAdditives}
        />
      ) : null}
    </Dialog>
  );
}
